/**
 * The sun's shadows of a storey's trees and bushes, for MapRenderingManager to draw and publish with
 * its buildings' shadows: one union, one strength, one softness, and the same shade on the bodies
 * standing in it (building_shade.ts). The shape is foliage_shadow_geometry.ts's.
 *
 * Only plants whose parent tile is marked `castsShadow` cast: art drawn WITHOUT a ground shadow (the
 * procedural grove, packages/tools/src/trees). The older LPC trees and bushes bake one in.
 */
import { NumberedTileMeta } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import type { ShadowPolygon } from "./building_shadow_geometry"
import { compositeSpritePlacement } from "./composite_tile_placement"
import { contentTextureGeneration } from "./content_textures"
import {
    FoliageShadowOptions, PlantShadowParts, PlantSilhouette, plantShadowParts, plantSilhouette, ShadowStamp, translatePolygon,
    translateStamp,
} from "./foliage_shadow_geometry"
import { compositeArtPixels } from "./sprites/composite_art_pixels"

/**
 * Live tuning, like window.buildingShadow: window.foliageShadow.enabled / .depth (how deep a shadow
 * lies for its width or drawn height) / .silhouettes (false: every plant casts the smooth outline).
 * Strength and softness are window.buildingShadow's: it is all one shadow. Setting
 * window.foliageShadow = false turns these off and leaves the buildings'.
 */
export const FOLIAGE_SHADOW = { enabled: true, depth: 0.5, silhouettes: true }

export const foliageShadowConfig = (): typeof FOLIAGE_SHADOW => {
    if (typeof window === "undefined") return FOLIAGE_SHADOW
    const w = window as any
    if (w.foliageShadow === undefined) w.foliageShadow = FOLIAGE_SHADOW
    const live = w.foliageShadow
    if (live === false) return { ...FOLIAGE_SHADOW, enabled: false }
    return live && typeof live === "object" ? live : FOLIAGE_SHADOW
}

/** A plant as its shadow needs it: where its art is in the atlas, and how big. */
interface Caster { meta: NumberedTileMeta; tilesetId: string; columns: number; width: number; height: number }

// Read once per plant variant and content generation (a Lab publish can repaint the art).
const silhouettes = new Map<string, PlantSilhouette>()
const shapes = new Map<string, PlantShadowParts>()
let artGeneration = -1

const plantShape = (caster: Caster, options: FoliageShadowOptions): PlantShadowParts | undefined => {
    const generation = contentTextureGeneration()
    if (generation !== artGeneration) {
        silhouettes.clear()
        shapes.clear()
        artGeneration = generation
    }
    const plant = `${caster.tilesetId}:${caster.meta.tileMeta.compositeTileId}`
    const key = `${plant}|${options.sun.x}:${options.sun.y}:${options.depth}:${options.silhouettes !== false}`
    const known = shapes.get(key)
    if (known) return known
    let art = silhouettes.get(plant)
    if (!art) {
        const pixels = compositeArtPixels(caster.tilesetId, caster.columns, caster.meta.children || [])
        if (!pixels) return undefined
        art = plantSilhouette(plant, pixels.data, pixels.width, pixels.height)
        silhouettes.set(plant, art)
    }
    const shape = plantShadowParts(art, options)
    shapes.set(key, shape)
    return shape
}

/** What a storey's plants cast: smooth outlines (spruces) and silhouettes (everything else). */
export interface FoliageShadows { polygons: ShadowPolygon[]; stamps: ShadowStamp[]; pending: boolean }

/**
 * The shadows of every shadow-casting plant drawn on this storey, in its pixels. `pending` while some
 * plant's art could not be read yet (its sheet still loading): ask again later.
 */
export const foliageShadows = (provider: TileMapMetaProvider, options: FoliageShadowOptions): FoliageShadows => {
    const polygons: ShadowPolygon[] = []
    const stamps: ShadowStamp[] = []
    let pending = false
    // Tile ids repeat across a forest: resolve each once.
    const casters = new Map<string, Caster | null>()
    const casterFor = (tile: string): Caster | null => {
        let caster = casters.get(tile)
        if (caster !== undefined) return caster
        caster = null
        const meta = provider.resolveTileMeta(tile)
        const tileset = meta?.castsShadow && meta.compositeTileParent && meta.compositeTileId ? provider.resolveTileSet(tile) : undefined
        const composite = tileset && provider.resolveCompositeTileIdTileMeta(meta.compositeTileId, tileset.tilesetId)
        if (composite) {
            const columns = tileset.width
            const cells = (composite.children || []).map(child => child.tileNumber - 1)
            const xs = cells.map(cell => cell % columns), ys = cells.map(cell => Math.floor(cell / columns))
            caster = { meta: composite, tilesetId: tileset.tilesetId, columns,
                width: Math.max(...xs) - Math.min(...xs) + 1, height: Math.max(...ys) - Math.min(...ys) + 1 }
        }
        casters.set(tile, caster)
        return caster
    }
    provider.visit(({ tilemap, layerId, sky }) => {
        if (!tilemap || sky) return
        tilemap.forEach((column, x) => column?.forEach((tile, y) => {
            if (!tile || tile === "0") return
            const caster = casterFor(tile)
            if (!caster || !provider.shouldDrawTileAt(x, y, layerId, tilemap)) return
            const shape = plantShape(caster, options)
            if (!shape) { pending = true; return }
            if (!shape.polygons.length && !shape.stamps.length) return
            const at = compositeSpritePlacement(caster.meta, caster, x, y)
            shape.polygons.forEach(polygon => polygons.push(translatePolygon(polygon, at.x, at.y)))
            shape.stamps.forEach(stamp => stamps.push(translateStamp(stamp, at.x, at.y)))
        }))
    })
    return { polygons, stamps, pending }
}
